import { Label } from '@kcftech/design-system';
import {
    SystemCreateRequest,
    SystemResponse,
    SystemUpdateRequest,
} from '@kcftech/prometheustypescriptapiclient';
import { MultiSelectOption } from 'components/forms/multiselect/MultiSelect';
import { MultiSelectTagList } from 'components/forms/multiselect/MultiSelectTagList';
import { Select } from 'components/forms/Select';
import { TextInput } from 'components/forms/TextInput';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { SerialNumber } from 'components/hardware/SerialNumber';
import { StatelessContextualModal } from 'components/modal/StatelessContextualModal';
import { InfoPopup } from 'components/popup/InfoPopup';
import { BaseStationsMutliSelect } from 'components/system/BaseStationsMultiSelect';
import { StaticSystemsMutliSelect } from 'components/system/StaticSystemsMultiSelect';
import { usePermissions } from 'contexts/PermissionsContext';
import { sdClient } from 'contexts/SDKClientContext';
import { useEffect, useState } from 'react';
import { Controller, RegisterOptions, useForm } from 'react-hook-form';

type SystemForm = {
    Name: string;
    BaseStations: string[];
    StaticSystems: string[];
};

const nameRules: RegisterOptions = {
    validate: (value) => (!value?.trim() ? 'Please enter a system name.' : undefined),
    required: 'Please enter a system name.',
    maxLength: {
        value: 50,
        message: 'System name cannot exceed 50 characters',
    },
};

const baseStationsRules: RegisterOptions = {
    required: 'Dynamic systems must have at least one base station selected.',
};

const staticSystemsRules: RegisterOptions = {
    required: 'Dynamic systems must have at least one static system selected.',
};

const collectorDependentSystemOptions = [
    {
        title: 'Static',
        value: 0,
    },
    {
        title: 'Dynamic',
        value: 1,
    },
];

export type CreateSystemModalProps = {
    systemToEdit?: SystemResponse;
    onSave: () => void;
    onCancel: () => void;
    locationId: string;
};

export function SystemSettingsModal({
    systemToEdit,
    onSave,
    onCancel,
    locationId,
}: CreateSystemModalProps) {
    const { canCreateDynamicSystem } = usePermissions();
    const [isCDS, setIsCDS] = useState(false);
    const [selectedBaseStations, setSelectedBaseStations] = useState<
        MultiSelectOption<string>[]
    >([]);
    const [selectedStaticSystems, setSelectedStaticSystems] = useState<
        MultiSelectOption<string>[]
    >([]);

    const {
        register,
        handleSubmit,
        control,
        formState: { errors, isValid, isSubmitting, isSubmitted, isValidating },
    } = useForm<SystemForm>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            Name: systemToEdit?.Name,
            BaseStations: systemToEdit?.CdsBaseStations.map(
                (b) => b.BaseStationSerialNumber
            ),
            StaticSystems: systemToEdit?.CdsMasterSystems.map((s) => s.Name),
        },
    });

    const onSaveSystem = async (data: SystemForm) => {
        const trimmedName = data.Name.trim();

        if (systemToEdit) {
            await sdClient.updateSystem({
                systemId: systemToEdit.Id,
                body: new SystemUpdateRequest({
                    Name: trimmedName,
                    Abbreviation: systemToEdit.Abbreviation,
                    IsPaused: systemToEdit.IsPaused,
                    Active: systemToEdit.Active,
                    CdsBaseStationIds: selectedBaseStations.map((b) => Number(b.value)),
                    CdsMasterSystemIds: selectedStaticSystems.map((s) => s.value),
                }),
            });
        } else {
            await sdClient.createSystem({
                body: new SystemCreateRequest({
                    Name: trimmedName,
                    LocationId: locationId,
                    IsCollectorDependentSystem: isCDS,
                    CdsBaseStationIds: selectedBaseStations.map((b) => Number(b.value)),
                    CdsMasterSystemIds: selectedStaticSystems.map((s) => s.value),
                }),
            });
        }
        onSave();
    };

    useEffect(() => {
        setSelectedBaseStations(
            systemToEdit?.CdsBaseStations.map((b) => ({
                title: `${b.BaseStationSerialNumber} (${b.BaseStationName})`,
                value: b.BaseStationId.toString(),
                label: (
                    <span>
                        <SerialNumber value={b.BaseStationSerialNumber} /> (
                        {b.BaseStationName})
                    </span>
                ),
            })) ?? []
        );
        setSelectedStaticSystems(
            systemToEdit?.CdsMasterSystems.map((sys) => ({
                title: sys.Name,
                value: sys.Id,
            })) ?? []
        );
    }, [systemToEdit]);

    return (
        <StatelessContextualModal
            id="system-modal"
            title={systemToEdit ? `Edit ${systemToEdit.Name}` : 'Create System'}
            overlay
            onClose={onCancel}
            saveLabel={systemToEdit ? 'Save' : 'Create'}
            canSave={(!isSubmitted || isValid) && !(isValidating || isSubmitting)}
            onSave={async () => {
                let successToken = false;
                const handler = handleSubmit(async (data) => {
                    await onSaveSystem(data);
                    successToken = true;
                });
                await handler();
                return successToken;
            }}
            content={() => (
                <div className="flex-column flex-grow-1">
                    <ValidatableLabel label="System Name" required error={errors.Name}>
                        <TextInput
                            autoFocus
                            placeholder="Enter a system name"
                            invalid={errors.Name !== undefined}
                            {...register('Name', nameRules)}
                        />
                    </ValidatableLabel>
                    <div className="flex-row align-center mt-20">
                        <Label required label="System Type" />
                        <InfoPopup title="System Type">
                            <p>
                                A static system is a system that is setup to connect to
                                hardware equipment that does not change unless it needs
                                replaced.
                            </p>
                            <p>
                                A dynamic system is a system that is setup to connect to
                                various pieces of hardware equipment that is constantly
                                changing due to the mobility of the system.
                            </p>
                        </InfoPopup>
                    </div>
                    {systemToEdit ? (
                        <>
                            {systemToEdit.IsCollectorDependentSystem
                                ? 'Dynamic'
                                : 'Static'}
                        </>
                    ) : (
                        <Select
                            options={
                                canCreateDynamicSystem
                                    ? collectorDependentSystemOptions
                                    : [collectorDependentSystemOptions[0]]
                            }
                            onChange={(e) => {
                                if (Number(e.target.value) === 0) {
                                    setIsCDS(false);
                                } else {
                                    setIsCDS(true);
                                }
                            }}
                        />
                    )}
                    {(isCDS || systemToEdit?.IsCollectorDependentSystem) && (
                        <>
                            <ValidatableLabel
                                label="Base Station(s)"
                                className="mt-20"
                                required
                                error={errors.BaseStations}
                            >
                                <Controller
                                    control={control}
                                    name="BaseStations"
                                    rules={baseStationsRules}
                                    render={({ field: { onChange } }) => (
                                        <>
                                            <BaseStationsMutliSelect
                                                locationId={locationId}
                                                baseStationIds={selectedBaseStations}
                                                onChange={(selected) => {
                                                    onChange(
                                                        selected.map(
                                                            (option) => option.value
                                                        )
                                                    );
                                                    setSelectedBaseStations(selected);
                                                }}
                                            />
                                            <MultiSelectTagList
                                                selectedOptions={selectedBaseStations}
                                                onChange={(selected) => {
                                                    onChange(
                                                        selected.map(
                                                            (option) => option.value
                                                        )
                                                    );
                                                    setSelectedBaseStations(selected);
                                                }}
                                            />
                                        </>
                                    )}
                                />
                            </ValidatableLabel>
                            <ValidatableLabel
                                label="Static System(s)"
                                className="mt-20"
                                required
                                error={errors.StaticSystems}
                            >
                                <Controller
                                    control={control}
                                    name="StaticSystems"
                                    rules={staticSystemsRules}
                                    render={({ field: { onChange } }) => (
                                        <>
                                            <StaticSystemsMutliSelect
                                                locationId={locationId}
                                                staticSystemsIds={selectedStaticSystems}
                                                onChange={(selected) => {
                                                    onChange(
                                                        selected.map(
                                                            (option) => option.value
                                                        )
                                                    );
                                                    setSelectedStaticSystems(selected);
                                                }}
                                            />
                                            <MultiSelectTagList
                                                selectedOptions={selectedStaticSystems}
                                                onChange={(selected) => {
                                                    onChange(
                                                        selected.map(
                                                            (option) => option.value
                                                        )
                                                    );
                                                    setSelectedStaticSystems(selected);
                                                }}
                                            />
                                        </>
                                    )}
                                />
                            </ValidatableLabel>
                        </>
                    )}
                </div>
            )}
        />
    );
}
